import { useAppSelector } from 'hooks/reduxHooks'
import { useMemo } from 'react'
import { isBundledCarHireEnabled } from 'selectors/carHireSelectors'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { addDays, dateIsAfter, dateIsBeforeOrSame } from 'lib/datetime/dateUtils'

interface Filters {
  pickUpLocationId?: string
  dropOffLocationId?: string
  pickUpDate?: string
  dropOffDate?: string
  pickUpTime?: string
  dropOffTime?: string
  driversAgeCategory?: App.CarHireDriverAgeCategory
  driversAge?: number
  isPickUpAtAirport?: boolean
  pickUpSearchType?: App.CarHireSearchTypes
  dropOffSearchType?: App.CarHireSearchTypes
  bundledOrderId?: string
}

function useCarHireOfferListFilters(filters: Filters) {
  const bundledCarHireEnabled = useAppSelector(state => isBundledCarHireEnabled(state))
  const finalFilters = useMemo((): App.CarHireOfferListFilters => {
    let pickUpDate = filters.pickUpDate
    const dropOffDate = filters.dropOffDate
    let bundledOrderId

    if (bundledCarHireEnabled && filters.bundledOrderId && pickUpDate && dropOffDate) {
      bundledOrderId = filters.bundledOrderId

      // pickUpDate for bundled car hire is the hotel checkin date so it can potentially be in the past if the customer
      // searches for cars during the reservation period, that's why we use day + 2 in this case.
      // If dropOffDate (hotel checkout date) has already passed we don't change the filters because we want the request for cars to fail (no availability)
      if (dateIsBeforeOrSame(new Date(), new Date(dropOffDate))) {
        if (dateIsAfter(new Date(), new Date(pickUpDate))) {
          pickUpDate = moment(addDays(new Date(), 2)).format(ISO_DATE_FORMAT)
        }
      }
    }

    return {
      pickUpLocationId: filters.pickUpLocationId,
      dropOffLocationId: filters.dropOffLocationId,
      pickUpDate,
      dropOffDate,
      pickUpTime: filters.pickUpTime,
      dropOffTime: filters.dropOffTime,
      driversAgeCategory: filters.driversAgeCategory,
      driversAge: filters.driversAge,
      isPickUpAtAirport: filters.isPickUpAtAirport,
      pickUpSearchType: filters.pickUpSearchType,
      dropOffSearchType: filters.dropOffSearchType,
      bundledOrderId,
    }
  }, [filters, bundledCarHireEnabled])

  return finalFilters
}

export default useCarHireOfferListFilters
